<template>
  <div class="curriculum-list-container">
    <div class="page-header">
      <div class="breadcrumb">ວິຊາການ / <span>ລາຍຊື່ວິຊາຮຽນ</span></div>
      <div class="header-content">
        <h1>ຈັດການລາຍຊື່ວິຊາຮຽນ</h1>
        <div class="header-actions">
          <button class="btn-export" @click="exportToExcel" :disabled="subjects.length === 0">
            <i class="fas fa-file-excel"></i> ສົ່ງອອກ Excel
          </button>
          <button v-if="hasBit('academic', 1)" class="btn-primary" @click="$router.push('/home/academic/curriculum/new')">
            <i class="fas fa-plus"></i> ເພີ່ມວິຊາໃໝ່
          </button>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <div class="search-box">
          <div class="input-container">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              @keyup.enter="handleSearch" 
              placeholder="ຄົ້ນຫາດ້ວຍຊື່ວິຊາ ຫຼື ລະຫັດ..."
            >
          </div>
          <button class="btn-search" @click="handleSearch">ຄົ້ນຫາ</button>
        </div>
      </div>

      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th width="80">ລຳດັບ</th>
                <th width="150">ລະຫັດວິຊາ</th>
                <th>ຊື່ວິຊາ (ພາສາລາວ)</th>
                <th class="text-center" width="150">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="4" class="text-center py-4"><i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດ...</td>
              </tr>
              <tr v-else v-for="(subject, index) in subjects" :key="subject._id">
                <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                <td><span class="asset-code">{{ subject.id }}</span></td>
                <td class="asset-name">{{ subject.subject_name_lao }}</td>
                <td class="text-center">
                  <button v-if="hasBit('academic', 1)" class="panel-action" @click="handleEdit(subject)" title="ແກ້ໄຂ">✏️</button>
                  <button v-if="hasBit('academic', 2)" class="panel-action text-danger" @click="showConfirm(subject)" title="ລົບ">🗑️</button>
                </td>
              </tr>
              <tr v-if="!loading && subjects.length === 0">
                <td colspan="4" class="text-center py-4">ບໍ່ມີຂໍ້ມູນວິຊາຮຽນ</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination-container">
        <div class="pagination-info">ສະແດງ {{ startItem }} - {{ endItem }} ຈາກ {{ totalRows }} ລາຍການ</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">«</button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'e' + idx" class="page-ellipsis">…</span>
            <button v-else :key="page"
                    class="page-btn"
                    :class="{ active: currentPage === page }"
                    @click="changePage(page)">{{ page }}</button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">»</button>
        </div>
      </div>
    </div>

    <!-- Modal ຢືນຢັນ (Confirm Modal) -->
    <transition name="modal">
      <div v-if="confirmModal.show" class="modal-overlay" @mousedown.self="confirmModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon warning">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="modal-content-area">
            <h2>ຢືນຢັນການລົບ</h2>
            <p>ທ່ານຕ້ອງການລົບວິຊາ <strong>{{ confirmModal.subjectName }}</strong> ອອກຈາກລະບົບແມ່ນບໍ່?</p>
          </div>
          <div class="modal-footer-actions">
            <button class="btn-modal-secondary" @click="confirmModal.show = false">ຍົກເລີກ</button>
            <button class="btn-modal-primary error" @click="handleDelete">ຕົກລົງ, ລົບອອກ</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal ແຈ້ງຜົນຮັບ (Result Modal) -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="resultModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type">
            <i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i>
          </div>
          <div class="modal-content-area">
            <h2>{{ resultModal.title }}</h2>
            <p>{{ resultModal.message }}</p>
          </div>
          <div class="modal-footer-actions">
            <button 
              class="btn-modal-primary" 
              :class="resultModal.type" 
              @click="resultModal.show = false"
            >
              ເຂົ້າໃຈແລ້ວ
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import * as XLSX from 'xlsx';
import { getPaginationRange } from '@/utils/pagination';

export default {
  name: 'CurriculumListPage',
  data() {
    return {
      subjects: [],
      currentPage: 1,
      pageSize: 15,
      totalRows: 0,
      searchQuery: '',
      loading: false,
      confirmModal: { show: false, subjectId: null, subjectName: '' },
      resultModal: { show: false, type: 'success', title: '', message: '' }
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.totalRows / this.pageSize) || 1; },
    visiblePages() { return getPaginationRange(this.currentPage, this.totalPages); },
    startItem() { return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    endItem() { 
      const end = this.currentPage * this.pageSize;
      return end > this.totalRows ? this.totalRows : end;
    }
  },
  mounted() {
    this.fetchSubjects();
  },
  methods: {
    async fetchSubjects() {
      this.loading = true;
      try {
        const res = await api.get('/listsubjects/all', {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery
          }
        });
        if (res.data.code === 1) {
          this.subjects = res.data.rows;
          this.totalRows = res.data.totalRows;
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchSubjects();
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchSubjects();
      }
    },
    handleEdit(subject) {
      this.$router.push(`/home/academic/curriculum/edit/${subject._id}`);
    },
    showConfirm(subject) {
      this.confirmModal = {
        show: true,
        subjectId: subject._id,
        subjectName: subject.subject_name_lao
      };
    },
    showResult(type, title, message) {
      this.resultModal = { show: true, type, title, message };
    },
    async handleDelete() {
      const subjectId = this.confirmModal.subjectId;
      this.confirmModal.show = false;
      try {
        const res = await api.delete(`/del/listsubjects/${subjectId}`);
        if (res.data.code === 1) {
          this.showResult('success', 'ລົບສຳເລັດ', 'ຂໍ້ມູນວິຊາຮຽນຖືກລົບອອກຈາກລະບົບແລ້ວ');
          this.fetchSubjects();
        }
      } catch (e) {
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດລົບຂໍ້ມູນວິຊາຮຽນນີ້ໄດ້');
      }
    },
    async exportToExcel() {
      if (this.subjects.length === 0) return;
      
      try {
        // ດຶງຂໍ້ມູນທັງໝົດ (ບໍ່ຈຳກັດ Pagination) ເພື່ອສົ່ງອອກ
        const res = await api.get('/listsubjects/all', {
          params: { page: 1, limit: 1000, search: this.searchQuery }
        });

        if (res.data.code === 1) {
          const allSubjects = res.data.rows;
          
          // ສ້າງ Header
          const header = ['ລຳດັບ', 'ລະຫັດວິຊາ', 'ຊື່ວິຊາ (ພາສາລາວ)'];
          
          // ຈັດຮູບແບບຂໍ້ມູນ
          const data = allSubjects.map((item, index) => [
            index + 1,
            item.id,
            item.subject_name_lao
          ]);

          const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "ລາຍຊື່ວິຊາຮຽນ");

          // ກຳນົດຊື່ໄຟລ໌
          const date = new Date().toISOString().split('T')[0];
          XLSX.writeFile(wb, `ລາຍຊື່ວິຊາຮຽນ_${date}.xlsx`);
          
          this.showResult('success', 'ສົ່ງອອກສຳເລັດ', 'ໄຟລ໌ Excel ຖືກສ້າງຮຽບຮ້ອຍແລ້ວ');
        }
      } catch (error) {
        console.error('Export error:', error);
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດສົ່ງອອກຂໍ້ມູນໄດ້');
      }
    }
  }
};
</script>

<style scoped>
.curriculum-list-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.header-actions { display: flex; gap: 12px; }

.btn-primary {
  background: var(--lao);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}
.btn-primary:hover { background: var(--lao-mid); }

.btn-export {
  background: #10b981;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.2s;
}
.btn-export:hover:not(:disabled) { background: #059669; transform: translateY(-1px); }
.btn-export:disabled { opacity: 0.6; cursor: not-allowed; }

.search-box { display: flex; gap: 10px; padding: 15px 20px; background: #fcfcfc; border-radius: 10px 10px 0 0; }
.input-container { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); }
.input-container input { width: 100%; padding: 10px 10px 10px 35px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; transition: border-color 0.2s; }
.input-container input:focus { outline: none; border-color: var(--lao); }
.btn-search { background: var(--lao); color: white; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; font-weight: 500; }

.table-wrap { overflow-x: auto; }
.table-wrap { overflow-x: auto; }
.asset-code { background: #e2e8f0; padding: 2px 8px; border-radius: 4px; font-family: 'IBM Plex Mono', monospace; font-size: 11px; font-weight: 600; color: #475569; }
.asset-name { color: var(--text1); font-weight: 500; }

/* Modal Overlay Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

/* Result & Confirm Modal Custom Styling */
.result-modal { padding: 40px 30px; max-width: 400px !important; text-align: center; background: white; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
.modal-icon { font-size: 64px; margin-bottom: 20px; }
.modal-icon.success { color: #10b981; }
.modal-icon.error { color: #ef4444; }
.modal-icon.warning { color: #f59e0b; }

.modal-content-area h2 { margin: 0 0 10px; font-size: 1.6rem; color: #0f172a; font-weight: 800; }
.modal-content-area p { color: #334155; font-size: 1.05rem; line-height: 1.6; margin-bottom: 30px; font-weight: 500; }

.modal-footer-actions { display: flex; gap: 12px; justify-content: center; }

.btn-modal-primary {
  padding: 12px 24px; border: none; border-radius: 10px; cursor: pointer;
  font-weight: 600; font-size: 14px; background: var(--lao); color: white;
  transition: 0.2s; min-width: 140px;
}
.btn-modal-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 63, 138, 0.2); }
.btn-modal-primary.success { background: #10b981; }
.btn-modal-primary.success:hover { background: #059669; }
.btn-modal-primary.error { background: #ef4444; }
.btn-modal-primary.error:hover { background: #dc2626; }

.btn-modal-secondary {
  padding: 12px 24px; border: 1.5px solid #e2e8f0; border-radius: 10px;
  cursor: pointer; font-weight: 600; font-size: 14px; background: white;
  color: #475569; transition: 0.2s; min-width: 120px;
}
.btn-modal-secondary:hover { background: #f8fafc; color: var(--text1); }

/* Modal Animation */
.modal-enter-active, .modal-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter-active .custom-modal-card { animation: modalIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.modal-leave-active .custom-modal-card { animation: modalOut 0.2s ease-in; }

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.8) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes modalOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.9); }
}

.pagination-container { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-top: 1px solid var(--border); }
.page-btn { padding: 5px 12px; border: 1px solid var(--border); background: white; border-radius: 4px; cursor: pointer; margin: 0 2px; transition: all 0.2s; }
.page-btn.active { background: var(--lao); color: white; border-color: var(--lao); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-ellipsis { padding: 5px 6px; color: var(--text3); }
.text-danger { color: var(--danger) !important; }
.text-center { text-align: center; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>